import useAuthStore from "../store/authStore";
import { Permissions } from "../constants/permissions";

export const usePermissions = () => {
  const {
    hasPermission,
    hasAllPermissions,
    hasAnyPermission,
    user,
    userPermissions,
  } = useAuthStore();

  return {
    // Individual permission checks
    canCreateUser: () => hasPermission(Permissions.USER_CREATE),
    canReadUser: () => hasPermission(Permissions.USER_READ),
    canUpdateUser: () => hasPermission(Permissions.USER_UPDATE),
    canDeleteUser: () => hasPermission(Permissions.USER_DELETE),
    canInviteUser: () => hasPermission(Permissions.USER_INVITE),

    canCreateRole: () => hasPermission(Permissions.ROLE_CREATE),
    canReadRole: () => hasPermission(Permissions.ROLE_READ),
    canUpdateRole: () => hasPermission(Permissions.ROLE_UPDATE),
    canDeleteRole: () => hasPermission(Permissions.ROLE_DELETE),

    canReadJob: () => hasPermission(Permissions.JOB_READ),
    canApplyJob: () => hasPermission(Permissions.JOB_APPLY),

    canCreateCoverLetter: () => hasPermission(Permissions.COVER_LETTER_CREATE),
    canReadCoverLetter: () => hasPermission(Permissions.COVER_LETTER_READ),

    canReadMessageBoard: () => hasPermission(Permissions.MESSAGE_BOARD_READ),
    canCreateMessageBoard: () =>
      hasPermission(Permissions.MESSAGE_BOARD_CREATE),

    canCreateInvitation: () => hasPermission(Permissions.INVITATION_CREATE),
    canReadInvitation: () => hasPermission(Permissions.INVITATION_READ),

    canReadAuditLog: () => hasPermission(Permissions.AUDIT_LOG_READ),
    canExportAuditLog: () => hasPermission(Permissions.AUDIT_LOG_EXPORT),

    canReadAnalytics: () => hasPermission(Permissions.ANALYTICS_READ),
    canExportAnalytics: () => hasPermission(Permissions.ANALYTICS_EXPORT),

    // Proposal permissions
    canCreateProposal: () => hasPermission(Permissions.PROPOSAL_CREATE),
    canReadProposal: () => hasPermission(Permissions.PROPOSAL_READ),
    canUpdateProposal: () => hasPermission(Permissions.PROPOSAL_UPDATE),
    canDeleteProposal: () => hasPermission(Permissions.PROPOSAL_DELETE),
    canApproveUpworkActions: () => hasPermission(Permissions.UPWORK_ACTION_APPROVE),
    canReviewProposals: () => hasPermission(Permissions.PROPOSAL_REVIEW),
    canSubmitProposal: () => hasPermission(Permissions.PROPOSAL_SUBMIT),

    // Portfolio permissions
    canCreatePortfolio: () => hasPermission(Permissions.PORTFOLIO_CREATE),
    canReadPortfolio: () => hasPermission(Permissions.PORTFOLIO_READ),
    canUpdatePortfolio: () => hasPermission(Permissions.PORTFOLIO_UPDATE),
    canDeletePortfolio: () => hasPermission(Permissions.PORTFOLIO_DELETE),

    // Vector data permissions
    canCreateVectorData: () => hasPermission(Permissions.VECTOR_DATA_CREATE),
    canReadVectorData: () => hasPermission(Permissions.VECTOR_DATA_READ),
    canUpdateVectorData: () => hasPermission(Permissions.VECTOR_DATA_UPDATE),
    canDeleteVectorData: () => hasPermission(Permissions.VECTOR_DATA_DELETE),

    // Tenant configuration permissions
    canReadTenantConfig: () => hasPermission(Permissions.TENANT_CONFIG_READ),
    canUpdateTenantConfig: () => hasPermission(Permissions.TENANT_CONFIG_UPDATE),
    canReadJobFilters: () => hasPermission(Permissions.JOB_FILTERS_READ),
    canUpdateJobFilters: () => hasPermission(Permissions.JOB_FILTERS_UPDATE),
    canReadUpworkCredentials: () => hasPermission(Permissions.UPWORK_CREDENTIALS_READ),
    canUpdateUpworkCredentials: () => hasPermission(Permissions.UPWORK_CREDENTIALS_UPDATE),
    canManageTenants: () => hasPermission(Permissions.SYSTEM_TENANT_MANAGE),

    // Admin permissions (combinations)
    canManageUsers: () =>
      hasAnyPermission([
        Permissions.USER_CREATE,
        Permissions.USER_READ,
        Permissions.USER_UPDATE,
        Permissions.USER_DELETE,
        Permissions.USER_INVITE,
      ]),

    canManageRoles: () =>
      hasAnyPermission([
        Permissions.ROLE_CREATE,
        Permissions.ROLE_READ,
        Permissions.ROLE_UPDATE,
        Permissions.ROLE_DELETE,
      ]),

    canAccessAdminPanel: () =>
      hasAnyPermission([
        Permissions.USER_CREATE,
        Permissions.USER_READ,
        Permissions.USER_UPDATE,
        Permissions.USER_DELETE,
        Permissions.USER_INVITE,
        Permissions.ROLE_CREATE,
        Permissions.ROLE_READ,
        Permissions.ROLE_UPDATE,
        Permissions.ROLE_DELETE,
        Permissions.INVITATION_CREATE,
        Permissions.INVITATION_READ,
        Permissions.AUDIT_LOG_READ,
        Permissions.AUDIT_LOG_EXPORT,
        Permissions.ANALYTICS_READ,
        Permissions.ANALYTICS_EXPORT,
        Permissions.PROPOSAL_REVIEW,
        Permissions.UPWORK_ACTION_APPROVE,
        Permissions.PORTFOLIO_CREATE,
        Permissions.PORTFOLIO_READ,
        Permissions.PORTFOLIO_UPDATE,
        Permissions.PORTFOLIO_DELETE,
        Permissions.VECTOR_DATA_CREATE,
        Permissions.VECTOR_DATA_READ,
        Permissions.VECTOR_DATA_UPDATE,
        Permissions.VECTOR_DATA_DELETE,
        Permissions.TENANT_CONFIG_READ,
        Permissions.TENANT_CONFIG_UPDATE,
        Permissions.JOB_FILTERS_READ,
        Permissions.JOB_FILTERS_UPDATE,
        Permissions.UPWORK_CREDENTIALS_READ,
        Permissions.UPWORK_CREDENTIALS_UPDATE,
        Permissions.SYSTEM_TENANT_MANAGE,
      ]),

    // Generic permission check
    hasPermission,
    hasAllPermissions,
    hasAnyPermission,

    // User info
    user,
    userPermissions,
    isSuperuser: user?.role === "superuser",
  };
};
